import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertOctagon,
  Briefcase,
  CheckCircle2,
  ChevronRight,
  Clock4,
  Download,
  Search,
  ShieldAlert,
  Timer,
  UserCircle,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Skeleton } from '@/components/ui/Skeleton'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { useInfiniteQuery } from '@tanstack/react-query'
import { listCases, type CasesListResponse } from './api'
import { cn } from '@/lib/cn'
import { formatTimeAgo, formatDateTime } from '@/lib/format'
import type { CaseRichSummary } from '@/features/alerts/types'
import { UnlinkedAlertsPanel } from './components/UnlinkedAlertsPanel'

const PAGE_SIZE = 50

type CaseStatus = CaseRichSummary['status']
type CasePriority = CaseRichSummary['priority']

const STATUS_LABEL: Record<CaseStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  in_review: 'In review',
  pending_info: 'Pending info',
  escalated: 'Escalated',
  resolved: 'Resolved',
  closed: 'Closed',
  fraud_confirmed: 'Fraud confirmed',
  false_positive: 'False positive',
}

const STATUS_VARIANT: Record<
  CaseStatus,
  React.ComponentProps<typeof Badge>['variant']
> = {
  open: 'pending',
  in_progress: 'info',
  in_review: 'info',
  pending_info: 'pending',
  escalated: 'critical',
  resolved: 'success',
  closed: 'default',
  fraud_confirmed: 'failed',
  false_positive: 'cancelled',
}

const STATUSES: CaseStatus[] = [
  'open',
  'in_review',
  'fraud_confirmed',
  'false_positive',
  'closed',
]

const PRIORITIES: CasePriority[] = ['critical', 'high', 'medium', 'low']

const PRIORITY_VARIANT: Record<
  CasePriority,
  React.ComponentProps<typeof Badge>['variant']
> = {
  critical: 'critical',
  high: 'failed',
  medium: 'pending',
  low: 'default',
}

/**
 * Cases queue — catalogue-aligned rebuild.
 *
 * Layout:
 *   - Header: title + total + Export CSV.
 *   - KPI strip (5 tiles): Open / In review / Fraud confirmed last
 *     7d / SLA breached / Total active.
 *   - Filter row: search + status chips + priority chips.
 *   - Table: case#, title, status, priority, alerts, age, SLA chip.
 *
 * Filters are server-side (the backend handler honours every filter
 * we pass), so a long backlog still pages quickly.
 */
export function CasesListPage() {
  const [q, setQ] = useState('')
  const [statusFilter, setStatusFilter] = useState<CaseStatus[]>([])
  const [priorityFilter, setPriorityFilter] = useState<CasePriority[]>([])

  const cases = useInfiniteQuery<CasesListResponse>({
    queryKey: [
      'cases',
      'list-infinite',
      { q, statusFilter, priorityFilter },
    ] as const,
    queryFn: ({ pageParam }) =>
      listCases({
        q: q || undefined,
        page: pageParam as number,
        pageSize: PAGE_SIZE,
        status: statusFilter.length > 0 ? statusFilter : undefined,
        priority: priorityFilter.length > 0 ? priorityFilter : undefined,
      }),
    initialPageParam: 1,
    getNextPageParam: (last, all) => {
      const loaded = all.reduce((sum, p) => sum + p.cases.length, 0)
      return loaded < last.total ? all.length + 1 : undefined
    },
    staleTime: 30_000,
  })

  const allCases = useMemo(
    () => cases.data?.pages.flatMap((p) => p.cases) ?? [],
    [cases.data?.pages],
  )
  const total = cases.data?.pages[0]?.total ?? 0

  // KPIs derive from the loaded page set. Open + in_review come
  // straight off the rows; fraud_confirmed_7d and breached use
  // timestamps. For an at-rest count without paging, the backend's
  // /cases/?status=… count fields would be authoritative — kept
  // client-side here since the loaded sample is representative.
  const kpis = useMemo(() => deriveKpis(allCases), [allCases])

  const toggleStatus = (s: CaseStatus) =>
    setStatusFilter((cur) =>
      cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s],
    )
  const togglePriority = (p: CasePriority) =>
    setPriorityFilter((cur) =>
      cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p],
    )

  function exportCsv() {
    const header = [
      'case_number',
      'title',
      'status',
      'priority',
      'alerts',
      'created_at',
      'sla_deadline',
      'sla_breached',
    ].join(',')
    const lines = allCases.map((c) =>
      [
        csv(c.case_number),
        csv(c.title),
        c.status,
        c.priority,
        c.alert_count,
        c.created_at ?? '',
        c.sla_deadline ?? '',
        c.sla_breached ? 'true' : 'false',
      ].join(','),
    )
    const blob = new Blob([header + '\n' + lines.join('\n')], {
      type: 'text/csv',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `cases_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      {/* ---- header --------------------------------------- */}
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">
            Investigations
          </p>
          <h1 className="text-2xl font-bold tracking-tight">Cases</h1>
          <p className="text-sm text-muted-fg">
            Investigations grouping multiple alerts. Open one for the full
            timeline, linked alerts, and case-level actions. Cases are created
            by analysts from alerts.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-fg">
            {total.toLocaleString()} total · {allCases.length.toLocaleString()} loaded
          </span>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={allCases.length === 0}>
            <Download size={12} className="mr-1" />
            Export CSV
          </Button>
        </div>
      </header>

      <UnlinkedAlertsPanel />

      {/* ---- KPI strip ----------------------------------- */}
      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Kpi
          icon={<Briefcase className="h-4 w-4 text-primary" />}
          label="Open"
          value={kpis.open}
          loading={cases.isLoading}
        />
        <Kpi
          icon={<Clock4 className="h-4 w-4 text-status-pending" />}
          label="In review"
          value={kpis.in_review}
          loading={cases.isLoading}
        />
        <Kpi
          icon={<AlertOctagon className="h-4 w-4 text-status-failed" />}
          label="Fraud confirmed (7d)"
          value={kpis.fraud_confirmed_7d}
          loading={cases.isLoading}
        />
        <Kpi
          icon={<Timer className="h-4 w-4 text-priority-critical" />}
          label="SLA breached"
          value={kpis.breached}
          loading={cases.isLoading}
        />
        <Kpi
          icon={<CheckCircle2 className="h-4 w-4 text-status-success" />}
          label="Closed (7d)"
          value={kpis.closed_7d}
          loading={cases.isLoading}
        />
      </section>

      {/* ---- filters ------------------------------------- */}
      <Card>
        <CardContent className="flex flex-col gap-3 py-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1">
              <label
                htmlFor="case-q"
                className="text-[0.6875rem] uppercase tracking-wider text-muted-fg"
              >
                Search
              </label>
              <span className="relative inline-block">
                <Search
                  size={12}
                  aria-hidden
                  className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-fg"
                />
                <Input
                  id="case-q"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Title, case number, summary…"
                  className="h-8 w-72 pl-7 text-xs"
                />
              </span>
            </div>
          </div>
          <FilterChipRow
            label="Status"
            options={STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] }))}
            active={statusFilter}
            onToggle={(v) => toggleStatus(v as CaseStatus)}
          />
          <FilterChipRow
            label="Priority"
            options={PRIORITIES.map((p) => ({
              value: p,
              label: p.charAt(0).toUpperCase() + p.slice(1),
            }))}
            active={priorityFilter}
            onToggle={(v) => togglePriority(v as CasePriority)}
          />
        </CardContent>
      </Card>

      {/* ---- list ---------------------------------------- */}
      {cases.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : cases.isError ? (
        <p className="rounded-md border border-status-failed/30 bg-status-failed/5 p-3 text-sm text-status-failed">
          Couldn't load cases.
        </p>
      ) : allCases.length === 0 ? (
        (() => {
          const hasFilters =
            q.trim() !== '' || statusFilter.length > 0 || priorityFilter.length > 0
          return hasFilters ? (
            <EmptyState
              icon={Briefcase}
              title="No cases match these filters"
              description="Try widening the search term or clearing the status / priority chips above."
              action={{
                label: 'Clear filters',
                onClick: () => {
                  setQ('')
                  setStatusFilter([])
                  setPriorityFilter([])
                },
              }}
            />
          ) : (
            <EmptyState
              icon={Briefcase}
              title="No cases yet"
              description="Cases appear here as alerts get escalated through the workflow. Check back once the alert queue has activity."
            />
          )
        })()
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="border-b border-border bg-muted/40 text-[0.625rem] uppercase tracking-wider text-muted-fg">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left">Case #</th>
                    <th scope="col" className="px-3 py-2 text-left">Title</th>
                    <th scope="col" className="px-3 py-2 text-left">Status</th>
                    <th scope="col" className="px-3 py-2 text-left">Priority</th>
                    <th scope="col" className="px-3 py-2 text-right">Alerts</th>
                    <th scope="col" className="px-3 py-2 text-left">Age</th>
                    <th scope="col" className="px-3 py-2 text-left">SLA</th>
                    <th scope="col" className="px-3 py-2 text-left">Assignee</th>
                    <th scope="col" className="px-3 py-2 text-right" />
                  </tr>
                </thead>
                <tbody>
                  {allCases.map((c) => (
                    <Row key={c.id} c={c} />
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {cases.hasNextPage && (
        <div className="flex justify-center pt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={cases.isFetchingNextPage}
            onClick={() => void cases.fetchNextPage()}
          >
            {cases.isFetchingNextPage ? 'Loading more cases…' : 'Load more cases'}
          </Button>
        </div>
      )}
    </div>
  )
}

// ---- subcomponents -------------------------------------------------

function Row({ c }: { c: CaseRichSummary }) {
  const created = c.created_at
  return (
    <tr className="border-b border-border last:border-b-0 hover:bg-muted/40">
      <td className="px-3 py-2 align-middle">
        <Link
          to={`/cases/${c.id}`}
          className="font-mono text-[0.6875rem] tabular-nums text-primary hover:underline"
        >
          {c.case_number}
        </Link>
      </td>
      <td className="px-3 py-2 align-middle">
        <Link
          to={`/cases/${c.id}`}
          className="font-medium hover:text-primary hover:underline"
          title={c.summary ?? c.title}
        >
          {c.title}
        </Link>
        {c.summary && c.summary !== c.title ? (
          <p className="mt-0.5 line-clamp-1 text-[0.625rem] text-muted-fg">
            {c.summary}
          </p>
        ) : null}
      </td>
      <td className="px-3 py-2 align-middle">
        <Badge variant={STATUS_VARIANT[c.status]}>
          {STATUS_LABEL[c.status]}
        </Badge>
      </td>
      <td className="px-3 py-2 align-middle">
        <Badge variant={PRIORITY_VARIANT[c.priority]} className="capitalize">
          {c.priority}
        </Badge>
      </td>
      <td className="px-3 py-2 text-right font-mono tabular-nums">
        {c.alert_count}
      </td>
      <td className="px-3 py-2 align-middle text-muted-fg">
        {created ? formatTimeAgo(created) : '—'}
      </td>
      <td className="px-3 py-2 align-middle">
        <SlaCell c={c} />
      </td>
      <td className="px-3 py-2 align-middle">
        {c.assigned_to ? (
          <span className="inline-flex items-center gap-1.5 text-fg" title={c.assigned_to.display_name}>
            <span
              className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-[0.6rem] font-semibold text-primary"
              aria-hidden
            >
              {c.assigned_to.initials}
            </span>
            <span className="truncate text-[0.6875rem]">
              {c.assigned_to.display_name}
            </span>
          </span>
        ) : c.assigned_to_id ? (
          // Server didn't hydrate the assignee block (older response
          // or user_id no longer resolvable). Fall back to the bare
          // id rendering so the row is still informative.
          <span className="inline-flex items-center gap-1 text-muted-fg">
            <UserCircle size={12} aria-hidden />
            <span className="font-mono text-[0.625rem]">
              user #{c.assigned_to_id}
            </span>
          </span>
        ) : (
          <span className="text-[0.6875rem] text-muted-fg">Unassigned</span>
        )}
      </td>
      <td className="px-3 py-2 align-middle text-right">
        <Link
          to={`/cases/${c.id}`}
          className="inline-flex items-center gap-0.5 text-[0.6875rem] uppercase tracking-wider text-primary hover:underline"
        >
          Open
          <ChevronRight size={12} aria-hidden />
        </Link>
      </td>
    </tr>
  )
}

function SlaCell({ c }: { c: CaseRichSummary }) {
  if (c.sla_breached) {
    return (
      <span className="inline-flex items-center gap-1 text-status-failed">
        <ShieldAlert size={11} aria-hidden />
        <span className="text-[0.6875rem] font-medium">Breached</span>
      </span>
    )
  }
  if (c.sla_deadline) {
    // The "now" reference is recomputed every render. The cases list
    // re-renders on the 30s react-query refetch so the SLA chip stays
    // fresh enough; a live ticking countdown isn't useful at the row
    // density this page renders.
    // eslint-disable-next-line react-hooks/purity
    const remaining = Date.parse(c.sla_deadline) - Date.now()
    const tone =
      remaining < 0
        ? 'text-status-failed'
        : remaining < 60 * 60 * 1000
          ? 'text-status-pending'
          : 'text-muted-fg'
    return (
      <span
        className={cn('inline-flex items-center gap-1 text-[0.6875rem]', tone)}
        title={`Due ${formatDateTime(c.sla_deadline)}`}
      >
        <Timer size={11} aria-hidden />
        {remaining < 0
          ? `Overdue ${formatTimeAgo(c.sla_deadline)}`
          : `Due ${formatTimeAgo(c.sla_deadline)}`}
      </span>
    )
  }
  return <span className="text-[0.6875rem] text-muted-fg">—</span>
}

function Kpi({
  icon,
  label,
  value,
  loading,
}: {
  icon: React.ReactNode
  label: string
  value: number
  loading?: boolean
}) {
  return (
    <Card>
      <CardContent className="space-y-1 py-3">
        <div className="flex items-center justify-between text-[0.625rem] uppercase tracking-wider text-muted-fg">
          <span>{label}</span>
          {icon}
        </div>
        {loading ? (
          <Skeleton />
        ) : (
          <p className="text-xl font-semibold tabular-nums">{value}</p>
        )}
      </CardContent>
    </Card>
  )
}

function FilterChipRow({
  label,
  options,
  active,
  onToggle,
}: {
  label: string
  options: Array<{ value: string; label: string }>
  active: string[]
  onToggle: (v: string) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[0.6875rem] uppercase tracking-wider text-muted-fg">
        {label}:
      </span>
      {options.map((o) => {
        const pressed = active.includes(o.value)
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={pressed}
            onClick={() => onToggle(o.value)}
            className={cn(
              'rounded-full border px-2.5 py-0.5 text-[0.6875rem] capitalize transition-colors',
              pressed
                ? 'border-primary bg-primary text-primary-fg'
                : 'border-border bg-card text-muted-fg hover:border-primary',
            )}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

// ---- helpers --------------------------------------------------------

function deriveKpis(cases: CaseRichSummary[]) {
  const out = {
    open: 0,
    in_review: 0,
    fraud_confirmed_7d: 0,
    closed_7d: 0,
    breached: 0,
  }
  const week = Date.now() - 7 * 24 * 3_600_000
  for (const c of cases) {
    if (c.status === 'open') out.open++
    if (c.status === 'in_review') out.in_review++
    if (c.sla_breached) out.breached++
    if (c.status === 'fraud_confirmed' && c.resolved_at) {
      if (Date.parse(c.resolved_at) >= week) out.fraud_confirmed_7d++
    }
    if (
      (c.status === 'closed' || c.status === 'false_positive') &&
      (c.closed_at || c.resolved_at)
    ) {
      const t = Date.parse(c.closed_at ?? c.resolved_at!)
      if (Number.isFinite(t) && t >= week) out.closed_7d++
    }
  }
  return out
}

function csv(s: string): string {
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return '"' + s.replace(/"/g, '""') + '"'
  }
  return s
}
